/**
 * WBTagalysSearchAdapter - turns the `details` Tagalys returns with every
 * search/recommendation response into the plain product-hit objects the
 * existing `product-tagalys-item` Vue component (vue-init.liquid /
 * vue-templates.liquid) already knows how to render, so Tagalys results
 * display without changing that component and without a Storefront round trip.
 * Fields produced: id, objectID (variant id), sku, handle, title, tags, price,
 * compare_at_price, variants_compare_at_price_max, available,
 * inventory_available, image, product_image, media.
 *
 * The component name is historical: it was built for the legacy search hits, and the
 * legacy search/PLP code it came with has since been removed.
 */
(function () {
  function toNumber(value) {
    var number = parseFloat(value);
    return isNaN(number) ? 0 : number;
  }

  // Cheapest available variant, falling back to cheapest overall.
  function pickVariant(variants) {
    var list = variants || [];
    var available = list.filter(function (v) { return v.available; });
    var pool = available.length ? available : list;
    return pool.reduce(function (cheapest, current) {
      if (!cheapest) return current;
      return toNumber(current.price) < toNumber(cheapest.price) ? current : cheapest;
    }, null);
  }

  // The hover image is images[offset]; the offset only depends on whether a
  // video sits at media position 1 for a few handles/tags. For every other
  // product the hover image is known from the response already, so it is put in
  // VueHoverImageManager's cache and its Storefront lookup only runs for the rest.
  function primeHoverImages(hits, detailsById) {
    var manager = window.VueHoverImageManager;
    if (!manager || !manager.cache || typeof manager.getHoverImageOffset !== 'function') return;
    hits.forEach(function (hit) {
      var offset = manager.getHoverImageOffset(hit.handle, hit.tags, false);
      if (offset !== manager.getHoverImageOffset(hit.handle, hit.tags, true)) return;
      var images = (detailsById[hit.id] && detailsById[hit.id].images) || [];
      manager.cache.set(hit.id, (images[offset] && images[offset].src) || null);
    });
  }

  // Hidden-product rule (window.wbIsHiddenProduct, layout/theme.liquid): every
  // hit list this adapter returns - search, recos, side cart, cart-cab/compare -
  // leaves those products out.
  function isListed(hit) {
    return !(typeof window.wbIsHiddenProduct === 'function' && window.wbIsHiddenProduct(hit.tags));
  }

  // v2 API products come in Shopify's product format (variants, images, tags).
  function shopifyProductToHit(product) {
    if (!product) return null;
    var variants = product.variants || [];
    var variant = pickVariant(variants);
    var image = (product.featured_image && product.featured_image.src) ||
      (product.images && product.images[0] && product.images[0].src) || '';
    return {
      id: String(product.id),
      objectID: variant ? String(variant.id) : String(product.id),
      sku: (variant && variant.sku) || '',
      handle: product.handle || '',
      title: product.title || '',
      tags: product.tags || [],
      available: !!product.available,
      inventory_available: !!product.available,
      price: variant ? toNumber(variant.price) : toNumber(product.price),
      compare_at_price: variant ? toNumber(variant.compare_at_price) : toNumber(product.compare_at_price),
      variants_compare_at_price_max: toNumber(product.compare_at_price_max),
      image: image,
      product_image: image,
      media: []
    };
  }

  // response: a v2 response requested with include[]=products. Returns product
  // hits in Tagalys' ranked order; options.filter (an optional hit predicate),
  // hidden products and holdback products (window.transformTagalysSearchResponse)
  // removed.
  function fromProducts(response, options) {
    options = options || {};
    var products = (response && response.products) || [];
    var detailsById = {};
    products.forEach(function (product) {
      detailsById[String(product.id)] = product;
    });
    var hits = products.map(shopifyProductToHit).filter(Boolean);
    if (options.filter) {
      hits = hits.filter(options.filter);
    }
    hits = hits.filter(isListed);
    hits = window.transformTagalysSearchResponse({ hits: hits }).hits;
    primeHoverImages(hits, detailsById);
    return hits;
  }

  var TAGGED_PRODUCTS_QUERY = 'query WBTaggedProducts($first: Int!, $query: String!) {' +
    ' products(first: $first, sortKey: BEST_SELLING, query: $query) { nodes {' +
    '  id handle title tags availableForSale' +
    '  featuredImage { url }' +
    '  priceRange { minVariantPrice { amount } }' +
    '  compareAtPriceRange { maxVariantPrice { amount } }' +
    '  variants(first: 1) { nodes { id sku } }' +
    ' } }' +
    '}';

  function numericId(gid) {
    var match = /(\d+)\s*$/.exec(String(gid || ''));
    return match ? match[1] : '';
  }

  // Products carrying ALL the given tags, best sellers first, as the same hit
  // objects - Shopify's replacement for a legacy "tags:a AND tags:b" search
  // on shops without an external search. The storefront tag search is exact (an unknown
  // tag returns nothing). Resolves to [] on failure.
  function productsWithTags(tags, first) {
    var list = (tags || []).filter(Boolean);
    if (!list.length || !window.WBStorefrontApi) {
      return Promise.resolve([]);
    }
    var query = list.map(function (tag) { return "tag:'" + String(tag).replace(/'/g, "\\'") + "'"; }).join(' AND ');
    return window.WBStorefrontApi.fetch(TAGGED_PRODUCTS_QUERY, { first: first || 50, query: query }).then(function (data) {
      var nodes = (data && data.products && data.products.nodes) || [];
      return nodes.map(function (node) {
        var variant = node.variants && node.variants.nodes && node.variants.nodes[0];
        var image = (node.featuredImage && node.featuredImage.url) || '';
        var compareAt = toNumber(node.compareAtPriceRange && node.compareAtPriceRange.maxVariantPrice.amount);
        return {
          id: numericId(node.id),
          objectID: variant ? numericId(variant.id) : numericId(node.id),
          sku: (variant && variant.sku) || '',
          handle: node.handle,
          title: node.title,
          tags: node.tags || [],
          available: !!node.availableForSale,
          inventory_available: !!node.availableForSale,
          price: toNumber(node.priceRange && node.priceRange.minVariantPrice.amount),
          compare_at_price: compareAt,
          variants_compare_at_price_max: compareAt,
          image: image,
          product_image: image,
          media: []
        };
      }).filter(isListed);
    }).catch(function (error) {
      console.error('[WBTagalysSearchAdapter] tagged products fetch failed', error);
      return [];
    });
  }

  window.WBTagalysSearchAdapter = {
    fromProducts: fromProducts,
    productsWithTags: productsWithTags
  };
})();
